/**
 * Incident runbooks: the recommended plan as steps the platform runs.
 * Backend: backend/internal/ams/runbook*.go.
 */
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, buildApiHeaders } from './api';

export type RunbookStepKind = 'auto' | 'approval' | 'input' | 'manual';
export type RunbookStepStatus =
  | 'pending'
  | 'running'
  | 'done'
  | 'failed'
  | 'needs_approval'
  | 'needs_input'
  | 'skipped';

export interface RunbookStep {
  id: string;
  phase: 'fix' | 'verify';
  title: string;
  detail?: string;
  kind: RunbookStepKind;
  capability?: string;
  args?: Record<string, unknown>;
  input?: {
    type: 'file' | 'text';
    label?: string;
    accept?: string[];
    columns?: string[];
    secret?: boolean;
    /** The argument this input fills when the incident did not state it. */
    param?: string;
  };
  needs?: string;
  status: RunbookStepStatus;
  output?: string;
  error?: string;
  attachment?: { id: string; name: string; size: number; valid: boolean; validation: string; preview?: string };
  value_set?: boolean;
  note?: string;
  actor?: string;
  approved_by?: string;
  updated_at?: string;
  /** What this step would act on, as the API computed it (ams.StepInputVersion); sent back when approving. */
  input_version?: string;
}

export interface Runbook {
  incident_id: string;
  source: 'analysis' | 'investigation';
  generated_at: string;
  steps: RunbookStep[];
}

const base = (projectId: string, incidentId: string) => `/projects/${projectId}/ams/incidents/${incidentId}/runbook`;
export const runbookKey = (projectId: string, incidentId: string) =>
  ['projects', projectId, 'ams', 'incidents', incidentId, 'runbook'] as const;

/** Steps still moving on their own: poll while any runs or a fix check waits
 * to auto-run (verify checks run once the fix steps are finished). */
export function runbookIsBusy(rb: Runbook | null | undefined): boolean {
  return Boolean(
    rb?.steps.some((s) => s.status === 'running' || (s.kind === 'auto' && s.status === 'pending' && s.phase === 'fix')),
  );
}

export function useIncidentRunbook(projectId: string, incidentId: string | null | undefined) {
  return useQuery({
    queryKey: runbookKey(projectId, incidentId || ''),
    // No runbook yet is `data: null`; never hand React Query `undefined` (it treats that as an error).
    queryFn: async () => (await apiClient<Runbook | null>(base(projectId, incidentId!))) ?? null,
    enabled: Boolean(projectId && incidentId),
    refetchInterval: (q) => (runbookIsBusy(q.state.data) ? 3000 : false),
  });
}

const runbookGenerateKey = (projectId: string, incidentId: string) => [...runbookKey(projectId, incidentId), 'generate'] as const;

/** True while any view has a runbook build in flight for this incident. */
export function useRunbookGenerating(projectId: string, incidentId: string): boolean {
  return useIsMutating({ mutationKey: runbookGenerateKey(projectId, incidentId) }) > 0;
}

function useRunbookMutation<TVars>(
  projectId: string,
  incidentId: string,
  fn: (vars: TVars) => Promise<Runbook | null>,
  mutationKey?: readonly unknown[],
) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey,
    mutationFn: fn,
    onSuccess: (rb) => {
      qc.setQueryData(runbookKey(projectId, incidentId), rb);
      void qc.invalidateQueries({ queryKey: runbookKey(projectId, incidentId) });
    },
    // A refusal (e.g. 409 INPUT_CHANGED) means this view is stale: show the step as it is now.
    onError: () => {
      void qc.invalidateQueries({ queryKey: runbookKey(projectId, incidentId) });
    },
  });
}

export function useRunbookActions(projectId: string, incidentId: string) {
  const url = base(projectId, incidentId);
  const post = (path: string, body?: unknown, timeoutMs = 60_000) =>
    apiClient<Runbook | null>(`${url}${path}`, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
      timeoutMs,
    });
  return {
    generate: useRunbookMutation(projectId, incidentId, () => post('/generate', undefined, 180_000), runbookGenerateKey(projectId, incidentId)),
    // Approving sends the input_version the approver was shown; the API runs
    // the step only if its inputs are still that version.
    run: useRunbookMutation(projectId, incidentId, (v: { stepId: string; inputVersion?: string }) =>
      post(`/steps/${v.stepId}/run`, v.inputVersion ? { input_version: v.inputVersion } : undefined, 180_000),
    ),
    finish: useRunbookMutation(projectId, incidentId, (v: { stepId: string; skip?: boolean; note?: string }) =>
      post(`/steps/${v.stepId}/finish`, { skip: Boolean(v.skip), note: v.note ?? '' }),
    ),
    value: useRunbookMutation(projectId, incidentId, (v: { stepId: string; value: string }) =>
      post(`/steps/${v.stepId}/input`, { value: v.value }),
    ),
    file: useRunbookMutation(projectId, incidentId, async (v: { stepId: string; file: File; path?: string }) => {
      const form = new FormData();
      form.append('file', v.file);
      if (v.path) form.append('path', v.path);
      const res = await fetch(`/api/v1${url}/steps/${v.stepId}/input`, {
        method: 'POST',
        body: form,
        headers: { ...buildApiHeaders() },
      });
      const wrapped = await res.json();
      if (!res.ok || !wrapped.success) throw new Error(wrapped.error?.message || `Upload failed (${res.status})`);
      return wrapped.data as Runbook;
    }),
  };
}

/** One-line summary of unfinished steps, for a closure note. */
export function unfinishedStepsNote(rb: Runbook | null | undefined): string {
  const open = rb?.steps.filter((s) => s.status !== 'done' && s.status !== 'skipped') ?? [];
  if (open.length === 0) return '';
  return `Unfinished runbook steps: ${open.map((s) => s.title).join('; ')}.`;
}
